import './styles.scss';

import { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent, ReactElement, Ref, forwardRef } from 'react';

import { TextareaTypes } from '@/common/Textarea/types';
import { getClasses } from '@/utils';
import { withValue } from '@/common/Textarea/utils';

const Textarea = forwardRef<TextareaTypes.RefType, TextareaTypes.Props>(
  (
    { onEnter, onTab, isValid, isInvalid, isDirty, pattern, autoSelect = true, ...props }: TextareaTypes.Props,
    ref: Ref<TextareaTypes.RefType>
  ): ReactElement => (
    <textarea
      {...props}
      className={getClasses(
        'Textarea',
        'Common', // For backward compatibility only. Do not use.
        'form-control', // Bootstrap class
        isValid !== undefined && isValid !== false ? 'is-valid' : undefined,
        isInvalid !== undefined && isInvalid !== false ? 'is-invalid' : undefined,
        isDirty !== undefined && isDirty !== false ? 'is-dirty' : undefined,
        props.className
      )}
      ref={ref}
      onBlur={withValue<FocusEvent<HTMLTextAreaElement>>(props.onBlur, props?.value)}
      onKeyUp={withValue<KeyboardEvent<HTMLTextAreaElement>>(props.onKeyUp, props?.value)}
      onClick={withValue<MouseEvent<HTMLTextAreaElement>>(props.onClick, props?.value)}
      onChange={(event: ChangeEvent<HTMLTextAreaElement>): void => {
        // If a pattern prop is provided, restrict input to matching characters or function output
        if (pattern && typeof event.target.value === 'string') {
          try {
            if (typeof pattern === 'function') {
              event.target.value = pattern(event.target.value);
            } else if (typeof pattern === 'string') {
              const regex = new RegExp(pattern, 'g');
              event.target.value = (event.target.value.match(regex) || []).join('');
            }
          } catch (err) {
            console.warn(`Invalid pattern prop provided to Textarea component: ${pattern}`, err);
          }
        }
        withValue<ChangeEvent<HTMLTextAreaElement>>(props.onChange, props?.value)(event);
      }}
      onFocus={(event: FocusEvent<HTMLTextAreaElement>): void => {
        if (autoSelect) event.target.select();
        withValue<FocusEvent<HTMLTextAreaElement>>(props.onFocus, props?.value)(event);
      }}
      onKeyDown={(event: KeyboardEvent<HTMLTextAreaElement>): void => {
        let fn = props.onKeyDown;
        if (event.key === 'Enter' && onEnter) fn = onEnter;
        if (event.key === 'Tab' && onTab) fn = onTab;
        withValue(fn, props?.value)(event);
      }}
    />
  )
);
Textarea.displayName = 'Textarea';

export default Textarea;
